/**
 * ConnectedCalendarsPanel.js
 * ─────────────────────────────────────────────────────────────────
 * Multi-account calendar management UI.
 * 
 * Features:
 *   - Lists ALL connected Google + Outlook accounts with status badges
 *   - Add another Google / Outlook account (connect multiple)
 *   - Toggle sync per account
 *   - Set primary account
 *   - Disconnect individual account
 *   - Force full resync button
 *   - Live sync count and status indicators
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, IconButton, Chip, Switch, Tooltip,
    CircularProgress, Skeleton, Divider, Paper, Badge, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, Snackbar, Fade, Menu, MenuItem, ListItemIcon,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import axiosInstance from '../../services/axiosInstance';

// ── Outlook Icon (SVG) ─────────────────────────────────────────────────────
const OutlookIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="3" fill="#0072C6" />
        <text x="4" y="17" fill="white" fontSize="13" fontWeight="bold" fontFamily="Arial">O</text>
    </svg>
);

// ── Status Dot ─────────────────────────────────────────────────────────────
const StatusDot = ({ status }) => {
    const colors = {
        synced: '#4caf50',
        syncing: '#ff9800',
        error: '#f44336',
        idle: '#9e9e9e',
    };
    return (
        <Box
            sx={{
                width: 8, height: 8,
                borderRadius: '50%',
                bgcolor: colors[status] || colors.idle,
                flexShrink: 0,
                boxShadow: status === 'syncing'
                    ? `0 0 0 3px ${colors.syncing}30`
                    : status === 'synced'
                        ? `0 0 0 3px ${colors.synced}20`
                        : 'none',
                animation: status === 'syncing' ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                    '0%': { boxShadow: `0 0 0 0px ${colors.syncing}50` },
                    '70%': { boxShadow: `0 0 0 6px ${colors.syncing}00` },
                    '100%': { boxShadow: `0 0 0 0px ${colors.syncing}00` },
                },
            }}
        />
    );
};

// ── Account Card ───────────────────────────────────────────────────────────
const AccountCard = ({ account, provider, onToggleSync, onSetPrimary, onDisconnect, onRefresh }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [toggling, setToggling] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);

    const isGoogle = provider === 'google';
    const accentColor = isGoogle ? '#ea4335' : '#0072C6';
    const bgColor = isGoogle ? 'rgba(234,67,53,0.07)' : 'rgba(0,114,198,0.07)';
    const statusLabel = account.syncStatus || 'idle';

    const handleToggle = async () => {
        setToggling(true);
        await onToggleSync(account.id, !account.syncEnabled);
        setToggling(false);
    };

    const handleDisconnect = async () => {
        setConfirmDisconnect(false);
        await onDisconnect(account.id);
    };

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: account.isPrimary ? `${accentColor}40` : '#e8eaf0',
                    borderRadius: 2.5,
                    p: 2,
                    transition: 'all 0.2s ease',
                    bgcolor: account.isPrimary ? bgColor : '#fafbfe',
                    '&:hover': {
                        borderColor: `${accentColor}60`,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                        transform: 'translateY(-1px)',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Provider accent bar */}
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                    background: account.syncEnabled
                        ? `linear-gradient(90deg, ${accentColor}, ${accentColor}88)`
                        : '#e0e0e0',
                    borderRadius: '2.5px 2.5px 0 0',
                    transition: 'background 0.3s',
                }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Icon */}
                    <Avatar sx={{
                        width: 38, height: 38,
                        bgcolor: isGoogle ? '#fff' : '#0072C6',
                        border: `1.5px solid ${isGoogle ? '#ea435330' : '#0072C630'}`,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    }}>
                        {isGoogle ? <GoogleIcon sx={{ color: '#ea4335', fontSize: 20 }} /> : <OutlookIcon />}
                    </Avatar>

                    {/* Account info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                            <Typography sx={{
                                fontWeight: 600, fontSize: '0.88rem', color: '#1a2035',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                maxWidth: { xs: 160, sm: 260 },
                            }}>
                                {account.email || `${isGoogle ? 'Google' : 'Outlook'} Account`}
                            </Typography>

                            {account.isPrimary && (
                                <Chip
                                    label="Primary" size="small"
                                    icon={<StarIcon sx={{ fontSize: '12px !important', color: `${accentColor} !important` }} />}
                                    sx={{
                                        height: 20, fontSize: '0.65rem', fontWeight: 700,
                                        bgcolor: `${accentColor}15`, color: accentColor,
                                        border: `1px solid ${accentColor}30`,
                                        '& .MuiChip-label': { px: 0.7 },
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3, flexWrap: 'wrap' }}>
                            <StatusDot status={statusLabel} />
                            <Typography sx={{ fontSize: '0.73rem', color: '#78909c', textTransform: 'capitalize' }}>
                                {statusLabel === 'synced' ? 'Synced' : statusLabel === 'syncing' ? 'Syncing...' : statusLabel === 'error' ? 'Error' : 'Idle'}
                            </Typography>
                            {account.lastSyncedAt && (
                                <>
                                    <Typography sx={{ fontSize: '0.65rem', color: '#b0bec5' }}>•</Typography>
                                    <Typography sx={{ fontSize: '0.73rem', color: '#b0bec5' }}>
                                        Last sync: {new Date(account.lastSyncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                        <Tooltip title={account.syncEnabled ? 'Sync enabled — click to disable' : 'Sync disabled — click to enable'} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {toggling ? (
                                    <CircularProgress size={18} sx={{ mx: 1 }} />
                                ) : (
                                    <Switch
                                        size="small"
                                        checked={account.syncEnabled}
                                        onChange={handleToggle}
                                        sx={{
                                            '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                            '& .MuiSwitch-track': {
                                                borderRadius: 8,
                                                bgcolor: account.syncEnabled ? `${accentColor}80` : undefined,
                                            },
                                            '& .Mui-checked .MuiSwitch-thumb': { bgcolor: accentColor },
                                            '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${accentColor}50 !important` },
                                        }}
                                    />
                                )}
                            </Box>
                        </Tooltip>

                        {!account.isPrimary && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onSetPrimary(account.id)}
                                sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    px: 1,
                                    py: 0.2,
                                    borderColor: `${accentColor}50`,
                                    color: accentColor,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: `${accentColor}10`, borderColor: accentColor }
                                }}
                            >
                                Set Primary
                            </Button>
                        )}
                        <IconButton
                            size="small"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{ color: '#90a4ae', '&:hover': { color: '#546e7a', bgcolor: '#f1f3f4' } }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Account menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 170 } }}
                >
                    {!account.isPrimary && (
                        <MenuItem onClick={() => { onSetPrimary(account.id); setAnchorEl(null); }} dense>
                            <ListItemIcon><StarBorderIcon fontSize="small" sx={{ color: accentColor }} /></ListItemIcon>
                            <Typography variant="body2">Set as Primary</Typography>
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => { onRefresh(account.id); setAnchorEl(null); }} dense>
                        <ListItemIcon><RefreshIcon fontSize="small" sx={{ color: '#1976d2' }} /></ListItemIcon>
                        <Typography variant="body2">Resync Now</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        onClick={() => { setConfirmDisconnect(true); setAnchorEl(null); }}
                        dense sx={{ color: '#f44336' }}
                    >
                        <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: '#f44336' }} /></ListItemIcon>
                        <Typography variant="body2" color="error">Disconnect</Typography>
                    </MenuItem>
                </Menu>
            </Paper>

            {/* Disconnect Confirm Dialog */}
            <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pt: 3, pb: 1 }}>
                    Disconnect {isGoogle ? 'Google' : 'Outlook'} Account?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{account.email}</strong> will be disconnected. Calendar events already synced will not be deleted,
                        but future syncing will stop for this account.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setConfirmDisconnect(false)} variant="outlined" size="small"
                        sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDisconnect} variant="contained" color="error" size="small"
                        sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Disconnect
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ── Provider Section ───────────────────────────────────────────────────────
const ProviderSection = ({ label, provider, accounts, onAddAccount, onToggleSync, onSetPrimary, onDisconnect, onRefreshAccount, loading }) => {
    const isGoogle = provider === 'google';
    const accentColor = isGoogle ? '#ea4335' : '#0072C6';
    const connected = accounts.length > 0;

    return (
        <Box sx={{ mb: 3.5 }}>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 28, height: 28, borderRadius: 1.5, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', bgcolor: isGoogle ? '#fce8e6' : '#e3f1fb',
                    }}>
                        {isGoogle
                            ? <GoogleIcon sx={{ color: '#ea4335', fontSize: 16 }} />
                            : <BusinessIcon sx={{ color: '#0072C6', fontSize: 16 }} />
                        }
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#1a2035' }}>
                        {label}
                    </Typography>
                    {connected && (
                        <Chip
                            label={`${accounts.length} account${accounts.length > 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 700,
                                bgcolor: `${accentColor}12`, color: accentColor,
                                border: `1px solid ${accentColor}25`,
                            }}
                        />
                    )}
                </Box>

                <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => onAddAccount(provider)}
                    sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                        color: accentColor, borderRadius: 2, py: 0.5, px: 1.5,
                        border: `1px solid ${accentColor}30`,
                        bgcolor: `${accentColor}08`,
                        '&:hover': { bgcolor: `${accentColor}16`, borderColor: `${accentColor}60` },
                    }}
                >
                    Add Account
                </Button>
            </Box>

            {/* Account list or empty state */}
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={76} sx={{ borderRadius: 2.5 }} />)}
                </Box>
            ) : accounts.length === 0 ? (
                <Paper elevation={0} sx={{
                    border: '1.5px dashed #e0e4ec', borderRadius: 2.5, p: 2.5,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    bgcolor: '#f9fafd', cursor: 'pointer',
                    '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}06` },
                    transition: 'all 0.2s',
                }} onClick={() => onAddAccount(provider)}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '50%', bgcolor: `${accentColor}12`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {isGoogle
                            ? <GoogleIcon sx={{ color: accentColor, fontSize: 22 }} />
                            : <BusinessIcon sx={{ color: accentColor, fontSize: 22 }} />
                        }
                    </Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#546e7a' }}>
                        Connect {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', textAlign: 'center' }}>
                        Click to connect your {isGoogle ? 'Google' : 'Microsoft'} account and start syncing
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    {accounts.map((account) => (
                        <AccountCard
                            key={account.id}
                            account={account}
                            provider={provider}
                            onToggleSync={onToggleSync}
                            onSetPrimary={onSetPrimary}
                            onDisconnect={onDisconnect}
                            onRefresh={onRefreshAccount}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

// ── Main Panel ─────────────────────────────────────────────────────────────
const ConnectedCalendarsPanel = ({ onStatusChange }) => {
    const [summary, setSummary] = useState({ google: { count: 0, accounts: [] }, outlook: { count: 0, accounts: [] } });
    const [loading, setLoading] = useState(true);
    const [resyncing, setResyncing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const userId = localStorage.getItem('userId');

    const showSnack = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const fetchSummary = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await axiosInstance.get('/calendar-accounts/summary', { params: { userId } });
            if (res.data.success) {
                setSummary(res.data.summary);
                if (onStatusChange) onStatusChange(res.data.summary);
            }
        } catch (err) {
            // Fallback: build summary from status
            try {
                const res = await axiosInstance.get('/calendar-accounts/status', { params: { userId } });
                if (res.data.summary) setSummary(res.data.summary);
            } catch (_) { }
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const handleAddAccount = async (provider) => {
        try {
            const endpoint = provider === 'google' ? '/calendar/auth-url' : '/outlook-calendar/auth-url';
            const res = await axiosInstance.get(endpoint);
            if (res.data.url) window.location.href = res.data.url;
        } catch (err) {
            showSnack(`Failed to initiate ${provider} connection`, 'error');
        }
    };

    const handleToggleSync = async (accountId, syncEnabled) => {
        try {
            await axiosInstance.post('/calendar-accounts/toggle-sync', {
                userId, accountId, syncEnabled,
            });
            showSnack(`Sync ${syncEnabled ? 'enabled' : 'disabled'}`);
            fetchSummary();
        } catch (err) {
            showSnack('Failed to update sync setting', 'error');
        }
    };

    const handleSetPrimary = async (accountId) => {
        try {
            await axiosInstance.post('/calendar-accounts/set-primary', { userId, accountId });
            showSnack('Primary account updated');
            fetchSummary();
        } catch (err) {
            showSnack('Failed to set primary account', 'error');
        }
    };

    const handleDisconnect = async (accountId) => {
        try {
            await axiosInstance.delete(`/calendar-accounts/${accountId}`, { params: { userId } });
            showSnack('Account disconnected successfully');
            fetchSummary();
        } catch (err) {
            showSnack('Failed to disconnect account', 'error');
        }
    };

    const handleRefreshAccount = async () => {
        setResyncing(true);
        try {
            await axiosInstance.post('/calendar-accounts/resync', { userId });
            showSnack('Resync initiated — this may take a moment');
            setTimeout(fetchSummary, 3000);
        } catch (err) {
            showSnack('Resync failed', 'error');
        } finally {
            setResyncing(false);
        }
    };

    const totalAccounts = (summary.google?.count || 0) + (summary.outlook?.count || 0);
    const syncingCount = [
        ...(summary.google?.accounts || []),
        ...(summary.outlook?.accounts || []),
    ].filter((a) => a.syncEnabled).length;

    return (
        <Box>
            {/* Panel Header */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                mb: 3, flexWrap: 'wrap', gap: 1.5,
            }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a2035', mb: 0.3 }}>
                        Connected Calendars
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#78909c' }}>
                        {totalAccounts === 0
                            ? 'No calendars connected yet. Connect Google or Outlook to start syncing.'
                            : `${totalAccounts} account${totalAccounts > 1 ? 's' : ''} connected · ${syncingCount} syncing`
                        }
                    </Typography>
                </Box>

                {totalAccounts > 0 && (
                    <Tooltip title="Resync all connected calendars now" arrow>
                        <Button
                            size="small"
                            startIcon={resyncing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={handleRefreshAccount}
                            disabled={resyncing}
                            sx={{
                                textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                                color: '#1565c0', borderRadius: 2, py: 0.7, px: 1.8,
                                border: '1px solid #bbdefb', bgcolor: '#e3f2fd',
                                '&:hover': { bgcolor: '#bbdefb' },
                            }}
                        >
                            {resyncing ? 'Syncing...' : 'Resync All'}
                        </Button>
                    </Tooltip>
                )}
            </Box>

            {/* Sync info banner */}
            {totalAccounts > 1 && (
                <Alert
                    severity="info"
                    icon={<SyncIcon fontSize="small" />}
                    sx={{
                        mb: 2.5, borderRadius: 2, fontSize: '0.78rem', py: 0.8,
                        bgcolor: '#e3f2fd', border: '1px solid #90caf9',
                        '& .MuiAlert-icon': { color: '#1565c0' },
                    }}
                >
                    <strong>Multi-calendar sync active.</strong> New meetings in any calendar are automatically mirrored
                    across all connected accounts. The CRM is always the master record.
                </Alert>
            )}

            {/* Google Section */}
            <ProviderSection
                label="Google Calendar"
                provider="google"
                accounts={summary.google?.accounts || []}
                onAddAccount={handleAddAccount}
                onToggleSync={handleToggleSync}
                onSetPrimary={handleSetPrimary}
                onDisconnect={handleDisconnect}
                onRefreshAccount={handleRefreshAccount}
                loading={loading}
            />

            <Divider sx={{ my: 1, borderColor: '#eef0f7' }} />

            {/* Outlook Section */}
            <ProviderSection
                label="Outlook / Teams"
                provider="outlook"
                accounts={summary.outlook?.accounts || []}
                onAddAccount={handleAddAccount}
                onToggleSync={handleToggleSync}
                onSetPrimary={handleSetPrimary}
                onDisconnect={handleDisconnect}
                onRefreshAccount={handleRefreshAccount}
                loading={loading}
            />

            {/* Sync behaviour explanation */}
            {totalAccounts > 0 && (
                <Box sx={{
                    mt: 2, p: 2, borderRadius: 2, bgcolor: '#f8f9fd',
                    border: '1px solid #e8eaf0',
                }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#546e7a', mb: 1 }}>
                        How sync works
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.75rem', color: '#78909c', mb: 0.5 } }}>
                        <li>Appointments created in the CRM are pushed to <strong>all</strong> connected calendars.</li>
                        <li>Events added externally (Google / Outlook) are auto-detected and synced back to the CRM.</li>
                        <li>Updates or deletions from any source are propagated everywhere within seconds.</li>
                        <li>The <strong>Primary</strong> account's Google Meet / Teams link is used for meeting links.</li>
                    </Box>
                </Box>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: 2, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ConnectedCalendarsPanel;
